/**
 * Technische Merkmale eines Artikels oder einer Produktfamilie.
 *
 * Dieselbe Komponente traegt beide Faelle -- wie schon bei den redaktionellen
 * Texten. Der Unterschied liegt allein im Bezug und darin, welche Merkmale
 * ueberhaupt zulaessig sind; das entscheidet der Katalog, nicht diese Datei.
 *
 * Zwei Grundsaetze bestimmen die Darstellung:
 *
 *   1. Im Lesemodus erscheint nur, was vorhanden ist. Bei 25 Merkmalen und
 *      typisch wenigen belegten waere eine Wand aus leeren Feldern das
 *      Gegenteil einer Uebersicht.
 *
 *   2. Es wird NICHTS von der Produktfamilie uebernommen. Ein fehlender
 *      Artikelwert bleibt fehlend. Eine geerbte Zahl saehe aus wie eine
 *      Messung dieses Artikels -- ein fehlender Text ist erkennbar unvollstaendig,
 *      eine falsche Zahl nicht.
 */
import { useCallback, useEffect, useState } from "react";
import type {
  ArticleFinding,
  MetaResponse, PropertyDefinition, PropertyGroup, PropertyMethod, PropertyOrigin,
  PropertyValue,
  ValueProposalRef,
} from "@homebase/shared";
import { formatCct, PROPERTY_METHODS, PROPERTY_ORIGINS, PROPERTY_GROUPS } from "@homebase/shared";
import { api } from "../lib/api.ts";
import { useI18n, type I18n } from "../i18n/index.tsx";
import { errorText } from "../i18n/errors.ts";
import { ErrorState, LoadingState } from "./States.tsx";
import "./ProductProperties.css";

/** Das Bleistiftzeichen. Als Konstante, damit es kein fest verdrahteter Text ist. */
const PENCIL = "\u270E";

export interface PropertySubject {
  readonly kind: "article" | "family" | "model";
  readonly id: number;
}

export function ProductProperties({ subject, meta, mayWrite, mayConfirm, groups, heading, proposals,
                                   onProposalDecided }: {
  subject: PropertySubject;
  meta: MetaResponse;
  /** Die Oberflaeche richtet sich danach; entschieden wird im Server. */
  mayWrite: boolean;
  mayConfirm: boolean;
  /**
   * Auf welche Merkmalsgruppen die Anzeige beschraenkt ist. Fehlt die Angabe,
   * erscheinen alle -- das ist die Arbeitsansicht „Technik".
   *
   * Die Produktakte gliedert dagegen nach der Lesereihenfolge der Preisliste,
   * und dort ist Lichttechnik ein eigener Abschnitt. Beschraenkt wird die
   * ANZEIGE, nicht der Katalog: welche Merkmale ueberhaupt zulaessig sind,
   * entscheidet weiterhin `property_definitions`.
   */
  groups?: readonly PropertyGroup[];
  /**
   * Die Ueberschrift. `null` heisst: keine -- dann traegt sie der umgebende
   * Abschnitt, und zwei gleichlautende Zeilen untereinander entfallen.
   */
  heading?: string | null;
  /**
   * Offene Aenderungsvorschlaege zu diesem Artikel.
   *
   * Sie kommen aus der Akte, nicht aus dieser Komponente: dieselbe Antwort
   * traegt Beziehungen und Vorschlaege, und zwei Abfragen fuer dieselbe
   * Aussage waeren zwei Wahrheiten.
   */
  proposals?: readonly ValueProposalRef[];
  /**
   * Ein Vorschlag wurde entschieden.
   *
   * Die Vorschlagsliste ist eine PROP -- sie stammt aus der Aktenantwort und
   * nicht aus dieser Ansicht. Nach einer Entscheidung laedt hier nur der
   * Merkmalswert neu; der entschiedene Vorschlag stuende ohne dieses Signal
   * weiter als offener Konflikt da, bis jemand die Seite neu laedt.
   */
  onProposalDecided?: () => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [items, setItems] = useState<readonly PropertyValue[] | null>(null);
  /*
   * Die Befunde des juengsten Pruefungslaufs -- nur fuer Artikel.
   *
   * Familien- und Modellwerte werden nicht geprueft; dort bleibt die Liste
   * leer, und die Zeilen sehen aus wie bisher.
   */
  const [befunde, setBefunde] = useState<readonly ArticleFinding[]>([]);
  /*
   * Die vom Produktmodell geerbten Werte einer SKU -- getrennt gehalten.
   *
   * Sie stehen in der Akte, weil sie fuer diese SKU gelten. Bearbeitet
   * werden sie hier NICHT: der Stift an einem geerbten Wert aenderte ihn am
   * Modell, also an allen Ausfuehrungen. Ein eigener Wert entsteht ueber
   * „Merkmal ergaenzen" und ersetzt den geerbten.
   */
  const [erbe, setErbe] = useState<{ readonly model: string; readonly items: readonly PropertyValue[] } | null>(null);
  const [error, setError] = useState<unknown>(null);
  /** Welches Merkmal ist gerade offen? Immer hoechstens eines. */
  const [editing, setEditing] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = subject.kind === "article" ? await api.articleProperties(subject.id)
              : subject.kind === "family"    ? await api.familyProperties(subject.id)
              :                                await api.modelProperties(subject.id);
      setItems(r.items);
      setBefunde("findings" in r ? r.findings : []);
      setErbe("inherited" in r && r.inherited
        ? { model: r.inherited.model.code, items: r.inherited.items } : null);
    } catch (e) { setError(e); }
  }, [subject.kind, subject.id]);

  useEffect(() => { setItems(null); setBefunde([]); setEditing(null); void load(); }, [load]);

  if (error !== null && !items) {
    return <div className="props"><ErrorState message={errorText(i18n, error)} onRetry={load} /></div>;
  }
  if (!items) return <div className="props"><LoadingState label={t("props.loading")} /></div>;

  // Nur Merkmale, die auf DIESER Ebene ueberhaupt gefuehrt werden duerfen --
  // und, wenn ein Ausschnitt verlangt ist, nur die seiner Gruppen.
  const allowed = meta.propertyDefinitions.filter((d) =>
    (subject.kind === "article" ? d.allowsArticle
     : subject.kind === "family" ? d.allowsFamily
     : d.allowsModel)
    && (groups === undefined || groups.includes(d.group as PropertyGroup)));
  const gruppen = groups ?? PROPERTY_GROUPS;
  const byDefinition = new Map<string, PropertyValue[]>();
  for (const v of [...items, ...(erbe?.items ?? [])]) {
    const list = byDefinition.get(v.definition) ?? [];
    list.push(v);
    byDefinition.set(v.definition, list);
  }
  const geerbt = new Set((erbe?.items ?? []).map((v) => v.definition));
  /* „Fehlend" heisst: ohne EIGENEN Wert. Ein geerbtes Merkmal laesst sich
     dadurch an der SKU ergaenzen -- und ueberschreibt dann den Modellwert. */
  const missing = allowed.filter((d) => !items.some((v) => v.definition === d.code));

  return (
    <div className="props">
      {heading !== null && (
        <h3 className="detail__section">
          {heading ?? t(subject.kind === "model" ? "props.sectionModel" : "props.section")}
        </h3>
      )}
      {subject.kind === "family" && <p className="props__note">{t("props.familyNote")}</p>}
      {/* Ein Modellmerkmal ist eine Aussage ueber ALLE Varianten. Es wird
          nicht auf die SKUs kopiert -- eine SKU zeigt es mit. */}
      {subject.kind === "model" && <p className="props__note">{t("props.modelNote")}</p>}
      {[...items, ...(erbe?.items ?? [])].filter((v) => allowed.some((d) => d.code === v.definition)).length === 0 && (
        <p className="props__empty">
          {t(subject.kind === "article" ? "props.empty"
           : subject.kind === "family"  ? "props.emptyFamily"
           :                              "props.emptyModel")}
        </p>
      )}

      {gruppen.map((group) => {
        const inGroup = allowed.filter((d) => d.group === group && byDefinition.has(d.code));
        if (inGroup.length === 0) return null;
        return (
          <section className="props__group" key={group}>
            {/*
              * Die Gruppenueberschrift entfaellt, wenn ueberhaupt nur EINE
              * Gruppe gezeigt wird: dann traegt der umgebende Abschnitt
              * denselben Namen, und „Lichttechnik" staende zweimal
              * untereinander.
              */}
            {gruppen.length > 1 && (
              <h4 className="props__grouptitle">{i18n.tCode("propGroup", group, group)}</h4>
            )}
            {inGroup.map((def) => (
              <PropertyRow
                key={def.code} def={def} values={byDefinition.get(def.code)!} i18n={i18n}
                vorschlaege={(proposals ?? []).filter((v) => v.definition === def.code)}
                onProposalDecided={onProposalDecided}
                befunde={befunde.filter((f) => f.definition === def.code)}
                mayWrite={mayWrite && !geerbt.has(def.code)}
                mayConfirm={mayConfirm && !geerbt.has(def.code)}
                geerbtVon={geerbt.has(def.code) ? erbe!.model : null}
                editing={editing} setEditing={setEditing}
                onChanged={() => { setEditing(null); void load(); }} onError={setError}
              />
            ))}
          </section>
        );
      })}

      {/* Fehlende Merkmale erscheinen erst beim Bearbeiten -- nicht als
          leeres Formular im Lesemodus. */}
      {mayWrite && missing.length > 0 && (
        <AddProperty
          missing={missing} i18n={i18n} subject={subject}
          open={editing === "__add"} setOpen={(v) => setEditing(v ? "__add" : null)}
          onChanged={() => { setEditing(null); void load(); }} onError={setError}
        />
      )}

      {error !== null && <p className="props__error" role="alert">{errorText(i18n, error)}</p>}
    </div>
  );
}

/**
 * Eine Zahl in der Schreibweise der Anzeigesprache.
 *
 * DE 11250 -> "11.250", EN -> "11,250". Die Nachkommastellen kommen aus dem
 * gespeicherten Wert und werden nicht aufgefuellt: steht dort `19.10`,
 * erscheint `19,10`; steht dort `150`, erscheint `150` und nicht `150,00`.
 * Die Datenbank haelt die Genauigkeit, die Anzeige erfindet keine dazu.
 */
/**
 * Die Einheit hinter einem Wert -- mit oder ohne Leerzeichen.
 *
 * Ein Gradzeichen haengt am Wert, jede andere Einheit steht daneben: `120°`,
 * aber `150 lm/W` und `4000 K`. Das ist keine Feinheit -- `120 °` liest sich
 * wie zwei Angaben.
 *
 * Die Regel stand bis zum 12.09.2026 nur hier und galt deshalb nur in der
 * Merkmalsansicht; die Modellachsen und die Produktanlage schrieben `85 °`.
 * Sie hat jetzt genau einen Ort, und die drei Ansichten rufen ihn.
 */
export function unitSuffix(unit: string | null): string {
  return unit ? (unit === "°" ? unit : ` ${unit}`) : "";
}

export function formatNumber(raw: string, lang: string): string {
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  const punkt = raw.indexOf(".");
  const stellen = punkt === -1 ? 0 : raw.length - punkt - 1;
  return n.toLocaleString(lang === "en" ? "en-GB" : "de-DE",
    { minimumFractionDigits: stellen, maximumFractionDigits: stellen });
}

/**
 * Anzeige eines Wertes -- Zahl, Bereich, Wahrheitswert oder Wertevorrat.
 *
 * Zwei bewusste Festlegungen:
 *
 *   1. Eine Schranke erscheint als TECHNISCHES ZEICHEN, nicht als Wort:
 *      `≤ 11.250 lm` statt `hoechstens 11.250 lm`. Das Zeichen ist in beiden
 *      Sprachen dasselbe, kuerzer und in der Branche das Uebliche. Wer den
 *      Wert vorliest, sagt weiterhin "hoechstens" -- wer ihn ueberfliegt,
 *      erkennt ihn schneller.
 *
 *   2. Zahlen folgen der Anzeigesprache. `11250 lm` ist eine Ziffernfolge,
 *      `11.250 lm` ist eine Zahl.
 */
export function displayValue(
  def: PropertyDefinition, v: PropertyValue, i18n: I18n,
  /**
   * Die uebliche Kurzform mitfuehren -- heute nur bei der Farbtemperatur
   * (`4.000 K (4K)`). In der Bearbeitung hilft sie beim Wiedererkennen; in der
   * ersten Produktansicht ist sie ein Synonym derselben Aussage und wird dort
   * abgeschaltet (Abschnitt 2 des Auftrags).
   */
  kurzform = true,
): string {
  const unit = unitSuffix(def.unit);
  const z = (raw: string): string => formatNumber(raw, i18n.lang);
  switch (def.valueType) {
    case "boolean":
      return i18n.t(v.bool ? "props.yes" : "props.no");
    case "enum":
      return i18n.tCode("propValue", v.code ?? "", v.code ?? "");
    case "text":
      return v.text ?? "";
    case "range": {
      /* Weder Untergrenze noch Obergrenze: dann gibt es nichts anzuzeigen.
         Frueher lief dieser Fall in `z(v.min!)` und warf -- eine leere Angabe
         darf eine Ansicht nicht zum Absturz bringen. */
      if (v.min === null && v.max === null) return "";
      if (v.min !== null && v.max !== null) {
        return v.min === v.max ? `${z(v.min)}${unit}` : `${z(v.min)}–${z(v.max)}${unit}`;
      }
      if (v.max !== null) return `≤ ${z(v.max)}${unit}`;
      return `≥ ${z(v.min!)}${unit}`;
    }
    default: {
      if (v.num === null) return "";
      /*
       * DIE SCHRANKE GEHOERT VOR DIE ZAHL.
       *
       * `value_operator` liegt seit Migration 0016 in der Tabelle -- und
       * wurde hier bis zum 16.09.2026 nicht angezeigt. Ein Blendwert, der
       * fachlich `< 19` lautet, stand als `19` da, und eine
       * Farbwiedergabe `> 90` als `90`.
       *
       * Das ist keine Kleinigkeit: `UGR 19` ist die STRENGERE Zusage als
       * `UGR < 19`, und in einer Ausschreibung entscheidet dieser
       * Unterschied. Der Wert war die ganze Zeit richtig gespeichert --
       * er war nur falsch zu lesen.
       *
       * Dasselbe Zeichen wie bei den Bereichen, aus demselben Grund: in
       * beiden Sprachen dasselbe, kuerzer, und in der Branche das
       * Uebliche.
       */
      const schranke = v.operator === "lt" ? "<" : v.operator === "le" ? "≤"
                     : v.operator === "gt" ? ">" : v.operator === "ge" ? "≥" : "";
      // Die Farbtemperatur erhaelt zusaetzlich ihre uebliche Kurzform.
      // Sie wird berechnet, nicht gespeichert.
      if (def.code === "cct" && kurzform && schranke === "") {
        return `${z(v.num)}${unit} (${formatCct(Number(v.num))})`;
      }
      return `${schranke}${z(v.num)}${unit}`;
    }
  }
}

function PropertyRow({ def, values, i18n, vorschlaege, befunde, mayWrite, mayConfirm, geerbtVon = null,
                      editing, setEditing, onChanged, onProposalDecided, onError }: {
  def: PropertyDefinition; values: PropertyValue[]; i18n: I18n;
  /** Der Code des Produktmodells, von dem die Werte dieser Zeile kommen. */
  geerbtVon?: string | null;
  vorschlaege: readonly ValueProposalRef[];
  mayWrite: boolean; mayConfirm: boolean;
  editing: string | null; setEditing: (k: string | null) => void;
  befunde: readonly ArticleFinding[];
  onChanged: () => void; onProposalDecided?: (() => void) | undefined; onError: (e: unknown) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const name = i18n.tCode("prop", def.code, def.code);
  const open = editing === def.code;
  // Mehrere Werte koennen aus demselben Segment stammen -- dann steht es
  // einmal da, nicht dreimal.
  const quellen = [...new Set(values.map((v) => v.sourceSegment).filter((q): q is string => !!q))];
  /* Welche Entscheidung laeuft gerade? Verhindert den doppelten Klick und
     macht sichtbar, dass etwas passiert. */
  const [laeuft, setLaeuft] = useState<number | null>(null);
  /*
   * DER FEHLER GEHOERT AN DIE ZEILE, DIE IHN AUSGELOEST HAT (PM003-UI-02).
   *
   * Bis hierher meldete jede Zeile ihren Fehler nach oben, und die
   * Merkmalspflege zeigte ihn in EINER Zeile ganz unten -- hinter allen
   * Gruppen und hinter dem Anlegen-Formular. An einem Produktmodell mit
   * dreissig Merkmalen liegt das mehrere Bildschirmhoehen unter dem
   * geoeffneten Editor. In der Abnahme sah es deshalb so aus, als tue der
   * Speichern-Knopf nichts: die Absage war da, nur nicht dort, wo jemand
   * hinsah.
   *
   * Die Meldung bleibt dieselbe -- derselbe Fehlercode, derselbe
   * uebersetzte Text, dasselbe `role="alert"`. Sie steht nur jetzt im
   * Editor, und damit ist auch „dieses Merkmal" eindeutig.
   */
  const [fehler, setFehler] = useState<unknown>(null);
  const melde = (e: unknown): void => { setFehler(e); onError(e); };
  const entscheide = async (id: number, wahl: "angenommen" | "abgelehnt"): Promise<void> => {
    setLaeuft(id);
    try {
      /*
       * ERST DER SERVER, DANN DIE ANSICHT.
       *
       * Kein optimistisches Ausblenden: schlaegt die Entscheidung fehl, muss
       * der Vorschlag stehen bleiben. Erst wenn sie durch ist, wird beides
       * neu geladen -- der Merkmalswert hier, die Vorschlagsliste beim
       * Aufrufer. Ohne das zweite blieb der entschiedene Vorschlag sichtbar,
       * und die Aktion sah aus, als haette sie nicht gewirkt.
       */
      await api.decideProposal(id, wahl);
      onChanged();
      onProposalDecided?.();
    }
    catch (e) { melde(e); }
    finally { setLaeuft(null); }
  };

  return (
    /*
     * EIN MERKMAL IST EIN BLOCK.
     *
     * Vorher trug die Wertzeile die Trennlinie, und Vorschlag, Herkunft und
     * Formular standen als Geschwister DAHINTER -- ausserhalb der Linie. Die
     * Quellangabe erschien dadurch optisch beim naechsten Merkmal.
     *
     * Jetzt umschliesst der Block alles, was zu einem Merkmal gehoert, und
     * traegt die Trennung. Das ist keine Kosmetik: wer eine Angabe dem
     * falschen Merkmal zuordnet, liest die Akte falsch.
     */
    /*
     * DER ANKER AM MERKMAL.
     *
     * Die Warnzone oben nennt betroffene Merkmale beim Namen. Damit man von
     * dort hierher springen kann, braucht die Zeile eine Kennung -- und
     * `scroll-margin-top`, damit sie nicht unter der Sprungleiste landet.
     */
    <div className="props__item" id={`merkmal-${def.code}`}>
      <div className="props__row">
        <div className="props__name">{name}</div>
        <div className="props__line">
          <div className="props__values">
            {values.map((v) => (
              <span className="props__value" key={v.id}>
                {displayValue(def, v, i18n)}
                {v.method === "abgeleitet" && (
                  <span className="props__unit"> · {i18n.tCode("propMethod", v.method, v.method)}</span>
                )}
              </span>
            ))}
          </div>
          <div className="props__meta">
            {/*
              * DER PRUEFSTAND STEHT NUR DA, WENN ER GELIEFERT WURDE.
              *
              * `quality` ist `null`, wenn dem Konto `property.review.read`
              * fehlt -- der Server liefert ihn dann gar nicht erst. Das ist
              * kein Sonderfall der Anzeige, sondern ihre einzige richtige
              * Auskunft: "Prüfung erforderlich" ist unsere Arbeitsnotiz und
              * keine Produktangabe.
              *
              * `null` heisst hier nie "kein Status" -- in der Datenbank ist
              * das Feld NOT NULL. Es heisst: nicht ausgeliefert.
              */}
            {geerbtVon !== null && (
              <span className="props__erbe" title={t("tk.inheritedTitle", { model: geerbtVon })}>
                {t("tk.inherited")}
              </span>
            )}
            {[...new Set(values.map((v) => v.quality))].filter((q) => q !== null).map((q) => (
              <span key={q} className={`props__q props__q--${q}`}>{i18n.tCode("quality", q, q)}</span>
            ))}
            {/*
              * ── FACHLICH BESTAETIGEN STEHT IN DER ZEILE (§20/§21) ──
              *
              * DER BEFUND DER ABNAHME. „Abdeckungsmaterial:
              * Polycarbonat" stand da, darueber „Prüfung
              * erforderlich" -- und es gab keinen Weg, den Wert zu
              * bestaetigen.
              *
              * DEN WEG GAB ES, UND ER WAR VERSTECKT: der Knopf lag
              * INNERHALB des Bearbeitungsformulars, erreichbar nur
              * ueber den Stift. Der Stift heisst „bearbeiten"; wer
              * bestaetigen will, aendert nichts und sucht ihn
              * deshalb nicht. Und er haengt an `mayWrite` -- ein
              * Konto mit `property.confirm` ohne Schreibrecht hatte
              * ueberhaupt keinen Zugang zu einer Handlung, die ihm
              * ausdruecklich erlaubt ist.
              *
              * DIE AKTION IST TYPUNABHAENGIG, und das ist der Grund,
              * sie HIER zu setzen: sie aendert nur `quality`, nicht
              * den Wert. Damit gilt sie fuer Zahl, Spanne, Text,
              * Enum, Wahrheitswert und Mehrfachwert gleich -- eine
              * Stelle statt sechs (§21).
              *
              * GEERBTE WERTE TRAGEN SIE NICHT. Ein vom Modell
              * geerbter Wert gehoert dem Modell; ihn von der SKU aus
              * zu bestaetigen hiesse, am falschen Gegenstand zu
              * entscheiden. Die Zeile sagt stattdessen „Modell" --
              * dort steht die Aktion.
              */}
            {mayConfirm && geerbtVon === null && (() => {
              const offen = values.filter((v) => v.quality !== null && v.quality !== "belastbar");
              const fest = values.filter((v) => v.quality === "belastbar");
              /* Eine Zeile mit mehreren Werten wird als Ganzes
                 bestaetigt -- sie ist EINE Angabe, auch wenn sie
                 mehrere Werte hat. */
              if (offen.length > 0) {
                return (
                  <button className="btn btn--small props__bestaetigen"
                    disabled={laeuft !== null}
                    aria-label={t("props.confirmAria", { name })}
                    onClick={() => void (async () => {
                      setLaeuft(offen[0]!.id);
                      try {
                        for (const v of offen) {
                          await api.updateProperty(v.id, { quality: "belastbar" });
                        }
                        onChanged();
                      } catch (e) { onError(e); } finally { setLaeuft(null); }
                    })()}>
                    {t("props.confirm")}
                  </button>
                );
              }
              if (fest.length > 0) {
                return (
                  <button className="btn btn--small props__bestaetigen"
                    disabled={laeuft !== null}
                    aria-label={t("props.unconfirmAria", { name })}
                    onClick={() => void (async () => {
                      setLaeuft(fest[0]!.id);
                      try {
                        for (const v of fest) {
                          await api.updateProperty(v.id, { quality: "warnung" });
                        }
                        onChanged();
                      } catch (e) { onError(e); } finally { setLaeuft(null); }
                    })()}>
                    {t("props.unconfirm")}
                  </button>
                );
              }
              return null;
            })()}
            {mayWrite && (
              <button className="props__pencil" title={t("props.edit")}
                aria-label={t("props.editAria", { name })}
                onClick={() => setEditing(open ? null : def.code)}>{PENCIL}</button>
            )}
          </div>
        </div>
      </div>
      {/*
        EIN OFFENER AENDERUNGSVORSCHLAG -- sichtbar, aber nicht uebernommen.
        
        Die neu strukturierte Masterpreisliste widerspricht an zwoelf Stellen
        dem Bestand. Diese Widersprueche stehen als `value_proposals` in der
        Datenbank, und bis hierher sah man sie in der Oberflaeche NIRGENDS:
        die Akte zeigte den alten Wert, als sei nichts gewesen.
        
        Der Vorschlag erscheint deshalb DIREKT unter dem Wert, dem er
        widerspricht -- nicht in einer eigenen Liste, die man erst suchen
        muss. Er ist die eine Stelle, an der die Quelle prominent sein DARF:
        ohne sie ist die Abweichung nicht zu beurteilen.
        
        Uebernommen wird nichts. Der Bestand bleibt unveraendert, bis ein
        Mensch entscheidet -- und der Schreibweg dafuer fehlt heute noch
        (Befund an 02).
      */}
      {/*
        * DER BEFUND STEHT AN SEINEM MERKMAL.
        *
        * Oben in der Akte nennt die Warnzone „Merkmale ausserhalb des
        * Modells: Farbwiedergabewert". Wer das liest, sieht DASS etwas ist --
        * und musste bisher raten, WAS. Der Pruefungslauf hat den Grund
        * bereits ausformuliert; er wird hier gezeigt, statt ihn zu
        * verschweigen.
        *
        * Ausdruecklich getrennt vom Pruefstand: „Prüfung erforderlich" heisst,
        * dass ein Wert noch nicht bestaetigt ist -- ein Befund heisst, dass
        * die Auswertung einen Widerspruch oder eine Modellgrenze gesehen hat.
        * Zwei verschiedene Dinge, zwei verschiedene Darstellungen.
        */}
      {befunde.map((f) => (
        <div key={f.findingCode} className={`props__befund props__befund--${f.findingClass}`}>
          <span className="props__befundart">
            {f.findingClass === "E" ? t("props.findingModel") : t("props.findingConflict")}
          </span>
          {f.detail && <span className="props__befundtext">{f.detail}</span>}
        </div>
      ))}

      {vorschlaege.length > 0 && (
        <div className="props__proposal">
          <div className="props__proprow">
            <span className="props__proplabel">{t("props.proposalCurrent")}</span>
            <span className="props__propvalue">
              {values.map((v) => displayValue(def, v, i18n)).join("  ·  ")}
            </span>
          </div>
          {vorschlaege.map((v) => (
            <div className={v.alreadyApplied
                              ? "props__proprow props__proprow--erledigt"
                              : "props__proprow"} key={v.id}>
              <span className="props__proplabel props__proplabel--neu">{t("props.proposalNew")}</span>
              <span className="props__propvalue props__propvalue--neu">
                {displayValue(def, {
                  id: -v.id, definition: def.code, valueIndex: 0,
                  num: v.valueNum, operator: v.valueOperator, min: v.valueMin, max: v.valueMax,
                  bool: null, code: v.valueCode, text: v.valueText,
                  origin: "import", method: "uebernommen", quality: "pruefung_erforderlich",
                  sourceSegment: null, updatedAt: "",
                }, i18n)}
              </span>
              <span className="props__propsource">{v.sourceNote ?? v.source}</span>
              {/*
                * DER VORSCHLAG IST GEGENSTANDSLOS -- ALSO KEINE SCHALTFLAECHE.
                *
                * Der vorgeschlagene Wert gilt bereits; jemand hat ihn ueber
                * die normale Merkmalspflege gesetzt, ohne diesen Vorschlag
                * anzufassen. Beide Wege sind vorgesehen, und keiner weiss vom
                * anderen.
                *
                * [Neue Referenz uebernehmen] hier stehen zu lassen, waere die
                * schlechteste von drei Moeglichkeiten: die Schaltflaeche
                * verspricht eine Aenderung, die nicht eintritt, und wer sie
                * betaetigt, traegt eine Entscheidung ins Protokoll, die
                * fachlich laengst anderswo gefallen ist.
                *
                * Der Vorschlag verschwindet trotzdem nicht. Er bleibt mit
                * Quelle und Wortlaut stehen -- er ist der Beleg dafuer, dass
                * die Masterliste an dieser Stelle etwas anderes sagte, und
                * das bleibt wahr.
                */}
              {v.alreadyApplied ? (
                <span className="props__propdone">{t("props.proposalAlready")}</span>
              ) : mayWrite ? (
                /*
                 * ZWEI HANDLUNGEN, JEDE AN IHREM VORSCHLAG.
                 *
                 * Bis hierher stand neben einem Vorschlag nur „Fachlich
                 * bestaetigen". Diese Schaltflaeche gehoert dem ALTEN Wert:
                 * wer sie betaetigte, erklaerte den bestehenden Wert fuer
                 * belastbar und liess den Vorschlag unberuehrt stehen. Wer
                 * einen Vorschlag entscheiden wollte, tat etwas anderes, als
                 * er glaubte.
                 *
                 * Die Schaltflaechen stehen deshalb IN der Vorschlagszeile,
                 * nicht darunter: bei mehreren Vorschlaegen zu einem Merkmal
                 * muss erkennbar bleiben, welcher entschieden wird.
                 */
                <span className="props__propactions">
                  <button className="btn btn--primary" disabled={laeuft !== null}
                    onClick={() => void entscheide(v.id, "angenommen")}>
                    {t("props.proposalAccept")}
                  </button>
                  <button className="btn" disabled={laeuft !== null}
                    onClick={() => void entscheide(v.id, "abgelehnt")}>
                    {t("props.proposalReject")}
                  </button>
                </span>
              ) : null}
            </div>
          ))}
          <p className="props__prophint">
            {mayWrite ? t("props.proposalHint") : t("props.proposalPending")}
          </p>
        </div>
      )}
      {/*
        DIE HERKUNFT BLEIBT VOLLSTAENDIG -- SIE STEHT NUR NICHT MEHR VORNE.
        
        Bis hierher stand der Quellwortlaut als eigene Zeile unter jedem Wert.
        Das war waehrend der Migration richtig: wer maschinell gewonnene Werte
        pruefen soll, muss sehen, WORAUS sie entstanden sind.

        Inzwischen traegt die Akte zweiundzwanzig Merkmale, und aus der
        Transparenzbruecke sind drei Zeilen je Merkmal geworden -- Label,
        Wert, QUELLANGABE. Die Seite las sich wie ein Pruefprotokoll, und der
        Wert selbst ging zwischen seinen Belegen unter.

        Sie ist deshalb aufklappbar und NICHT entfernt: ein Klick, keine
        zweite Ansicht, kein Nachladen. `<details>` statt eigener Zustand --
        es ist genau das Verhalten, das das Element beschreibt, und es ist
        ohne Zutun tastaturbedienbar.
      */}
      {quellen.length > 0 && (
        <details className="props__prov">
          <summary className="props__provsummary">{t("props.sourceSegment")}</summary>
          <p className="props__source">{quellen.join("  ·  ")}</p>
        </details>
      )}
      {open && (
        <>
          <PropertyForm
            def={def} values={values} i18n={i18n} mayConfirm={mayConfirm}
            onCancel={() => { setFehler(null); setEditing(null); }}
            onDone={onChanged} onError={melde}
          />
          {fehler !== null && (
            <p className="props__error props__error--zeile" role="alert" data-testid="props-zeilenfehler">
              {errorText(i18n, fehler)}
            </p>
          )}
        </>
      )}
    </div>
  );
}

/** Eingabefelder passend zum Wertetyp. */
function ValueFields({ def, i18n, state, set }: {
  def: PropertyDefinition; i18n: I18n;
  state: Record<string, string | boolean>; set: (k: string, v: string | boolean) => void;
}): React.JSX.Element {
  const t = i18n.t;
  switch (def.valueType) {
    case "boolean":
      return (
        <label className="props__field">
          <span>{t("props.value")}</span>
          <input type="checkbox" checked={state["bool"] === true}
            onChange={(e) => set("bool", e.target.checked)} />
        </label>
      );
    case "enum":
      return (
        <label className="props__field">
          <span>{t("props.value")}</span>
          <select value={String(state["code"] ?? "")} onChange={(e) => set("code", e.target.value)}>
            <option value="">{t("props.chooseValue")}</option>
            {def.enumValues.map((v) => (
              <option key={v.code} value={v.code}>{i18n.tCode("propValue", v.code, v.code)}</option>
            ))}
          </select>
        </label>
      );
    case "range":
      return (
        <>
          <label className="props__field">
            <span>{t("props.valueFrom")}{def.unit ? ` (${def.unit})` : ""}</span>
            <input inputMode="decimal" value={String(state["min"] ?? "")}
              onChange={(e) => set("min", e.target.value)} />
          </label>
          <label className="props__field">
            <span>{t("props.valueTo")}{def.unit ? ` (${def.unit})` : ""}</span>
            <input inputMode="decimal" value={String(state["max"] ?? "")}
              onChange={(e) => set("max", e.target.value)} />
          </label>
        </>
      );
    case "text":
      return (
        <label className="props__field">
          <span>{t("props.value")}</span>
          <input value={String(state["text"] ?? "")} onChange={(e) => set("text", e.target.value)} />
        </label>
      );
    default:
      return (
        <label className="props__field">
          <span>{t("props.value")}{def.unit ? ` (${def.unit})` : ""}</span>
          <input inputMode="decimal" value={String(state["num"] ?? "")}
            onChange={(e) => set("num", e.target.value)} />
        </label>
      );
  }
}

function PropertyForm({ def, values, i18n, mayConfirm, onCancel, onDone, onError }: {
  def: PropertyDefinition; values: PropertyValue[]; i18n: I18n;
  mayConfirm: boolean; onCancel: () => void; onDone: () => void; onError: (e: unknown) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const first = values[0]!;
  const [state, setState] = useState<Record<string, string | boolean>>({
    num: first.num ?? "", min: first.min ?? "", max: first.max ?? "",
    bool: first.bool ?? false, code: first.code ?? "", text: first.text ?? "",
    /* Das Formular oeffnet nur, wer schreiben darf -- und wer schreiben darf,
       bekommt Herkunft und Methode geliefert. Die Ausweichwerte sind deshalb
       unerreichbar und stehen nur da, weil der Typ sie zulaesst. */
    origin: first.origin ?? "intern", method: first.method ?? "erfasst",
  });
  const [busy, setBusy] = useState(false);
  const set = (k: string, v: string | boolean): void => setState((s) => ({ ...s, [k]: v }));

  async function run(fn: () => Promise<unknown>): Promise<void> {
    setBusy(true); onError(null);
    try { await fn(); onDone(); } catch (e) { onError(e); } finally { setBusy(false); }
  }

  const payload = (): Record<string, unknown> => {
    const base: Record<string, unknown> = {
      origin: state["origin"] as PropertyOrigin, method: state["method"] as PropertyMethod };
    switch (def.valueType) {
      case "boolean": return { ...base, bool: state["bool"] === true };
      case "enum":    return { ...base, code: state["code"] };
      case "text":    return { ...base, text: state["text"] };
      case "range":   return { ...base,
        min: state["min"] === "" ? null : state["min"], max: state["max"] === "" ? null : state["max"] };
      default:        return { ...base, num: state["num"] };
    }
  };

  return (
    <div className="props__form">
      <div className="props__fields">
        <ValueFields def={def} i18n={i18n} state={state} set={set} />
        <label className="props__field">
          <span>{t("props.origin")}</span>
          <select value={String(state["origin"])} onChange={(e) => set("origin", e.target.value)}>
            {PROPERTY_ORIGINS.map((o) => (
              <option key={o} value={o}>{i18n.tCode("textOrigin", o, o)}</option>
            ))}
          </select>
        </label>
        <label className="props__field">
          <span>{t("props.method")}</span>
          <select value={String(state["method"])} onChange={(e) => set("method", e.target.value)}>
            {PROPERTY_METHODS.map((m) => (
              <option key={m} value={m}>{i18n.tCode("propMethod", m, m)}</option>
            ))}
          </select>
        </label>
      </div>

      {first.sourceSegment && (
        <p className="props__hint">{t("props.sourceSegment")}: {first.sourceSegment}</p>
      )}
      {first.quality === "belastbar" && <p className="props__hint">{t("props.confirmHint")}</p>}

      <div className="props__formactions">
        <button className="btn btn--primary btn--small" disabled={busy}
          onClick={() => void run(() => api.updateProperty(first.id, payload()))}>
          {busy ? t("props.saving") : t("props.save")}
        </button>
        <button className="btn btn--small" onClick={onCancel}>{t("common.cancel")}</button>
        {/* Bestaetigen ist ein eigenes Recht -- der Server prueft es erneut. */}
        {mayConfirm && first.quality !== "belastbar" && (
          <button className="btn btn--small" disabled={busy}
            onClick={() => void run(() => api.updateProperty(first.id, { quality: "belastbar" }))}>
            {t("props.confirm")}
          </button>
        )}
        {mayConfirm && first.quality === "belastbar" && (
          <button className="btn btn--small" disabled={busy}
            onClick={() => void run(() => api.updateProperty(first.id, { quality: "warnung" }))}>
            {t("props.unconfirm")}
          </button>
        )}
        <button className="btn btn--small props__danger" disabled={busy}
          onClick={() => { if (window.confirm(t("props.removeConfirm"))) void run(() => api.deleteProperty(first.id)); }}>
          {t("props.remove")}
        </button>
      </div>
    </div>
  );
}

function AddProperty({ missing, i18n, subject, open, setOpen, onChanged, onError }: {
  missing: readonly PropertyDefinition[]; i18n: I18n; subject: PropertySubject;
  open: boolean; setOpen: (v: boolean) => void;
  onChanged: () => void; onError: (e: unknown) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [code, setCode] = useState(missing[0]!.code);
  const [state, setState] = useState<Record<string, string | boolean>>({ origin: "intern", method: "erfasst" });
  const [busy, setBusy] = useState(false);
  const def = missing.find((d) => d.code === code) ?? missing[0]!;
  const set = (k: string, v: string | boolean): void => setState((s) => ({ ...s, [k]: v }));

  if (!open) {
    return (
      <button className="btn props__add" onClick={() => setOpen(true)}>{t("props.add")}</button>
    );
  }

  async function save(): Promise<void> {
    setBusy(true); onError(null);
    const base: Record<string, unknown> = {
      definition: def.code,
      ...(subject.kind === "article" ? { articleId: subject.id }
        : subject.kind === "family"    ? { familyId: subject.id }
        :                                { modelId: subject.id }),
      origin: state["origin"], method: state["method"],
    };
    const body = def.valueType === "boolean" ? { ...base, bool: state["bool"] === true }
      : def.valueType === "enum" ? { ...base, code: state["code"] }
      : def.valueType === "text" ? { ...base, text: state["text"] }
      : def.valueType === "range" ? { ...base,
          min: state["min"] === "" || state["min"] === undefined ? null : state["min"],
          max: state["max"] === "" || state["max"] === undefined ? null : state["max"] }
      : { ...base, num: state["num"] };
    try { await api.createProperty(body as never); setState({ origin: "intern", method: "erfasst" }); onChanged(); }
    catch (e) { onError(e); } finally { setBusy(false); }
  }

  return (
    <div className="props__form">
      <div className="props__fields">
        <label className="props__field">
          <span>{t("props.chooseProperty")}</span>
          <select value={code} onChange={(e) => { setCode(e.target.value); setState({ origin: "intern", method: "erfasst" }); }}>
            {missing.map((d) => (
              <option key={d.code} value={d.code}>{i18n.tCode("prop", d.code, d.code)}</option>
            ))}
          </select>
        </label>
        <ValueFields def={def} i18n={i18n} state={state} set={set} />
        <label className="props__field">
          <span>{t("props.origin")}</span>
          <select value={String(state["origin"])} onChange={(e) => set("origin", e.target.value)}>
            {PROPERTY_ORIGINS.map((o) => (
              <option key={o} value={o}>{i18n.tCode("textOrigin", o, o)}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="props__formactions">
        <button className="btn btn--primary btn--small" disabled={busy} onClick={() => void save()}>
          {busy ? t("props.saving") : t("props.save")}
        </button>
        <button className="btn btn--small" onClick={() => setOpen(false)}>{t("common.cancel")}</button>
      </div>
    </div>
  );
}
